import React, { useState, useCallback } from 'react';
import { ArrowBack, Close, CloudUpload } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
    Checkbox,
    Collapse,
    Container,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
    FormLabel,
    RadioGroup,
    Radio,
    Button,
    Box,
    Divider,
} from '@mui/material';
import AttributesTable from './AttributesTable';
import CompetitionRules from './CompetitionRules';
import CustomSnackbar from '../../CustomSnackbar';
import { createCompetition, fetchCompetitionAttributes } from '../../services/Competition';
import CompetitionDateSection from './CompetitionDateSection';

const CompetitionCreateForm = () => {
    const navigate = useNavigate();
    const [publish, setPublish] = useState(false);
    const [module, setModule] = useState('');
    const [errors, setErrors] = useState({});
    // const [trackingValue, setTrackingValue] = useState('');
    const [joiningMethod, setJoiningMethod] = useState('manual');
    const [trackingAttribute, setTrackingAttribute] = useState('');
    const [criteriaFunction, setCriteriaFunction] = useState('lcb_score');
    const [trackingWindow, setTrackingWindow] = useState('');
    const [addParticipantRewards, setAddParticipantRewards] = useState(false);
    const [criteriaFunctionForRewards, setCriteriaFunctionForRewards] = useState('');
    const [trackingWindowForRewards, setTrackingWindowForRewards] = useState('');
    const [bannerFile, setBannerFile] = useState(null);
    const [formData, setFormData] = useState({
        competition_name: '',
        competition_start_date: '',
        competition_end_date: '',
        enrollment_start_date: '',
        enrollment_end_date: '',
        competition_description: '',
        competition_prize_details: '',
    });

    const [requiredAttributes, setRequiredAttributes] = useState({});
    const [competitionRules, setCompetitionRules] = useState([]);
    const [status, setStatus] = useState('');
    const [moduleAttributes, setModuleAttributes] = useState([]);
    const [noOfParticipants, setNoOfParticipants] = useState('');

    const handleModuleChange = useCallback((event) => {
        const selectedModule = event.target.value;
        setModule(selectedModule);
        setRequiredAttributes({}); // Reset attributes when module changes
        if (selectedModule) {
            fetchCompetitionAttributes(selectedModule)
                .then((response) => {
                    console.log('Fetched competition attributes:', response);
                    setModuleAttributes(response.attributes || []);
                })
                .catch((error) => {
                    console.error('Error fetching competition attributes:', error);
                    setStatus('Failed to fetch module attributes.');
                    setModuleAttributes([]);
                });
        } else {
            setModuleAttributes([]);
        }
    }, []);

    const handleTrackingAttributeChange = useCallback((event) => {
        setTrackingAttribute(event.target.value);
    }, []);

    const handleFormChange = useCallback(
        (field) => (event) => {
            setFormData((prev) => ({
                ...prev,
                [field]: event.target.value,
            }));
        },
        []
    );

    const handleBannerChange = useCallback((event) => {
        const file = event.target.files[0];
        if (file) {
            const img = new Image();
            img.onload = () => {
                if (img.width === 1200 && img.height === 300) {
                    setBannerFile(file);
                } else {
                    setStatus('Banner image must be 1200x300 pixels.');
                }
            };
            img.src = URL.createObjectURL(file);
        }

    }, []);

    const handleRulesChange = useCallback((rules) => {
        setCompetitionRules(Array.isArray(rules) ? rules : []);
    }, []);

    const handleAttributesChange = useCallback(({ required_attributes }) => {
        setRequiredAttributes(required_attributes);
        console.log('Updated required attributes:', required_attributes);
    }, []);

    const handleCreateCompetition = useCallback(async () => {
        // Validate required fields
        const requiredFields = [
            { field: formData.competition_name, name: 'Competition Name' },
            { field: formData.competition_start_date, name: 'Competition Start Date' },
            { field: formData.competition_end_date, name: 'Competition End Date' },
            ...(formData.enrollment_start_date || formData.enrollment_end_date
                ? [
                    { field: formData.enrollment_start_date, name: 'Enrollment Start Date' },
                    { field: formData.enrollment_end_date, name: 'Enrollment End Date' },
                ]
                : []),
            ...(addParticipantRewards
                ? [
                    { field: criteriaFunctionForRewards, name: 'Participant Criteria Function' },
                    { field: trackingWindowForRewards, name: 'Participant Tracking Window' },
                ]
                : []),
        ];

        const missingFields = requiredFields.filter(({ field }) => !field);
        if (missingFields.length > 0) {
            const missingFieldNames = missingFields.map(({ name }) => name).join(', ');
            setStatus(`Please fill in the following required fields: ${missingFieldNames}`);
            return;
        }

        // Prepare FormData for multipart submission
        const formDataPayload = new FormData();
        formDataPayload.append('competition_name', formData.competition_name);
        formDataPayload.append('competition_start_date', formData.competition_start_date);
        formDataPayload.append('competition_end_date', formData.competition_end_date);
        if (formData.enrollment_start_date) {
            formDataPayload.append('enrollment_start_date', formData.enrollment_start_date);
        }
        if (formData.enrollment_end_date) {
            formDataPayload.append('enrollment_end_date', formData.enrollment_end_date);
        }
        if (formData.competition_description) {
            formDataPayload.append('competition_description', formData.competition_description);
        }
        if (competitionRules.length > 0) {
            competitionRules.forEach((rule) => {
                formDataPayload.append('competition_rules[]', rule);
            });
        }
        if (formData.competition_prize_details) {
            formDataPayload.append('competition_prize_details', formData.competition_prize_details);
        }
        if (module) {
            formDataPayload.append('module', module);
        }
        if (Object.keys(requiredAttributes).length > 0) {
            formDataPayload.append('required_attributes', JSON.stringify(requiredAttributes));
        }
        formDataPayload.append('joining_method', joiningMethod);
        if (trackingAttribute) {
            formDataPayload.append('tracking_attribute', trackingAttribute);
        }
        // if (trackingValue) {
        //     formDataPayload.append('tracking_values', trackingValue);
        // }
        if (criteriaFunction) {
            formDataPayload.append('criteria_function', criteriaFunction);
        }
        if (trackingWindow) {
            formDataPayload.append('tracking_window', trackingWindow);
        }
        if (addParticipantRewards) {
            formDataPayload.append(
                'participant_attributes',
                JSON.stringify({
                    criteria_function: criteriaFunctionForRewards === 'count' ? noOfParticipants : criteriaFunctionForRewards,
                    tracking_window: trackingWindowForRewards,
                })
            );
        }
        formDataPayload.append('status', publish ? 'active' : 'draft');
        if (bannerFile) {
            formDataPayload.append('banner', bannerFile);
        }

        console.log('Payload prepared for submission:');
        for (const [key, value] of formDataPayload.entries()) {
            console.log(`${key}:`, value);
        }

        try {
            const data = await createCompetition(formDataPayload);
            console.log('Competition created successfully:', data);
            setStatus(data.message || 'Competition created successfully!');
            //wait a moment before navigating
            setTimeout(() => {
                navigate('/competitions');
            }, 1500);
        } catch (error) {
            console.error('Error creating competition:', error);
            setStatus(error.message || 'Failed to create competition.');
            setErrors(error.errors || {});
        }
    }, [
        formData,
        competitionRules,
        requiredAttributes,
        module,
        joiningMethod,
        trackingAttribute,
        // trackingValue,
        criteriaFunction,
        trackingWindow,
        addParticipantRewards,
        criteriaFunctionForRewards,
        trackingWindowForRewards,
        publish,
        bannerFile,
        navigate,
    ]);

    return (
        <React.Fragment>
            <Container maxWidth="lg" sx={{ mt: { xs: 3, sm: 3, md: 0 }, mb: { xs: 3, sm: 3, md: 0 } }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            sx={{ mr: 1 }}
                            onClick={() => navigate(-1)}
                        >
                            <ArrowBack />
                        </IconButton>
                        Create a New Competition
                    </Typography>

                    <FormControlLabel
                        control={<Switch checked={publish} onChange={() => setPublish(!publish)} />}
                        label={publish ? 'Active' : 'Draft'}
                    />
                </Stack>

                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 2 }}>
                    Fill all the (*) required fields.
                </Typography>

                <Paper elevation={0} sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <TextField
                                required
                                label="Competition Name"
                                placeholder="e.g., Best short film 2025"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={formData.competition_name}
                                onChange={handleFormChange('competition_name')}
                                error={!!errors.competition_name}
                                helperText={errors.competition_name?.[0] || ''}
                            />
                        </Grid>
                        <Grid size={12}>
                            <CompetitionDateSection formData={formData} setFormData={setFormData} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                required
                                label="Description"
                                placeholder="Provide a brief description of the competition"
                                fullWidth
                                variant="outlined"
                                size="small"
                                multiline
                                rows={4}
                                value={formData.competition_description}
                                onChange={handleFormChange('competition_description')}
                                error={!!errors.competition_description}
                                helperText={errors.competition_description?.[0] || ''}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                required
                                label="Competition Prizes"
                                placeholder="Outline the prizes for the competition"
                                fullWidth
                                variant="outlined"
                                size="small"
                                multiline
                                rows={4}
                                value={formData.competition_prize_details}
                                onChange={handleFormChange('competition_prize_details')}
                                error={!!errors.competition_prize_details}
                                helperText={errors.competition_prize_details?.[0] || ''}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <CompetitionRules onRulesChange={handleRulesChange} errors={errors} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="body1" fontWeight="bold" mb={1.5}>
                                Competition Banner
                            </Typography>
                            <Box
                                sx={{
                                    border: '2px dashed',
                                    borderColor: bannerFile ? 'primary.main' : 'grey.400',
                                    borderRadius: 2,
                                    p: 3,
                                    textAlign: 'center',
                                    position: 'relative',
                                    transition: 'border-color 0.3s',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                    },
                                }}
                            >
                                {!bannerFile ? (
                                    <Button
                                        component="label"
                                        role={undefined}
                                        variant="text"
                                        color='inherit'
                                        disableElevation
                                        sx={{ textTransform: 'none' }}
                                    >
                                        <Stack spacing={1} alignItems="center">
                                            <CloudUpload sx={{ fontSize: 40, color: 'text.secondary' }} />
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                Upload Banner Image <span style={{ color: 'red' }}>*</span>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Recommended size: 1200×300 px (JPG or PNG)
                                            </Typography>
                                        </Stack>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleBannerChange}
                                        />
                                    </Button>
                                ) : (
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Box
                                            component="img"
                                            src={URL.createObjectURL(bannerFile)}
                                            alt="Banner Preview"
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: 2,
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => setBannerFile(null)}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                            }}
                                        >
                                            <Close />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                            {errors.banner && (
                                <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                    {errors.banner[0]}
                                </Typography>
                            )}
                        </Grid>

                        <Divider sx={{ width: '100%', my: 2 }} />

                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel id="demo-select-joining-method">Joining Method</InputLabel>
                                <Select
                                    label="Joining Method"
                                    labelId="demo-select-joining-method"
                                    value={joiningMethod}
                                    onChange={(event) => setJoiningMethod(event.target.value)}
                                >
                                    <MenuItem value="manual">Manual</MenuItem>
                                    <MenuItem value="auto" disabled>Auto</MenuItem>
                                </Select>

                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, md: 9 }}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel id="demo-select-small-label">Select Module</InputLabel>
                                <Select
                                    value={module}
                                    onChange={handleModuleChange}
                                    label="Select Module"
                                    labelId="demo-select-small-label"
                                >
                                    <MenuItem value="">
                                        <em>Select Module</em>
                                    </MenuItem>
                                    <MenuItem value="Project">Project</MenuItem>
                                    <MenuItem value="Profile" disabled>Profile</MenuItem>
                                    <MenuItem value="Post" disabled>Post</MenuItem>
                                </Select>
                                {errors.module && (
                                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                        {errors.module[0]}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>


                        <Grid size={12}>
                            {module && (
                                <AttributesTable
                                    onAttributesChange={handleAttributesChange}
                                    module={module}
                                    moduleAttributes={moduleAttributes}
                                />
                            )}
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel id="demo-select-small-label">Tracking Attribute</InputLabel>
                                <Select
                                    labelId="demo-select-small-label"
                                    id="demo-select-small"
                                    value={trackingAttribute}
                                    label="Tracking Attribute"
                                    onChange={handleTrackingAttributeChange}
                                >
                                    <MenuItem value="" disabled>
                                        <em>None</em>
                                    </MenuItem>
                                    {module === 'Project' && (
                                        <MenuItem value="Rating">Rating</MenuItem>
                                    )}
                                    <MenuItem value="Reaction" disabled>Reaction</MenuItem>
                                    <MenuItem value="Comment" disabled>Comment</MenuItem>
                                    <MenuItem value="Share" disabled>Share</MenuItem>
                                </Select>
                                {errors.tracking_attribute && (
                                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                        {errors.tracking_attribute[0]}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>


                        {/* <Grid size={{ xs: 12, md: 6 }}>
                             <TextField
                                size="small"
                                fullWidth
                                label="Tracking Value"
                                placeholder="e.g., Win"
                                value={trackingValue}
                                onChange={(event) => setTrackingValue(event.target.value)}
                            /> 
                        </Grid> */}




                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth required>
                                <FormLabel id="demo-radio-buttons-group-label">Criteria Function</FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    name="radio-buttons-group"
                                    value={criteriaFunction}
                                    onChange={(event) => setCriteriaFunction(event.target.value)}
                                >
                                    <FormControlLabel disabled value="sum" control={<Radio />} label="Sum" />
                                    <FormControlLabel disabled value="average" control={<Radio />} label="Average" />
                                    <FormControlLabel value="lcb_score" control={<Radio />} label="LCB Score" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <FormControl fullWidth required>
                                <FormLabel id="demo-radio-buttons-group-label">Tracking Window</FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    name="radio-buttons-group"
                                    value={trackingWindow}
                                    onChange={(event) => setTrackingWindow(event.target.value)}
                                >
                                    <FormControlLabel value="forever" control={<Radio />} label="Forever" />
                                    <FormControlLabel value="competition" control={<Radio />} label="Competition" />
                                    <FormControlLabel value="registration" control={<Radio />} label="Registration" />
                                </RadioGroup>
                                {errors.tracking_window && (
                                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                        {errors.tracking_window[0]}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid size={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={addParticipantRewards}
                                        onChange={() => setAddParticipantRewards(!addParticipantRewards)}
                                    />
                                }
                                label="Evaluators Reward"
                            />
                        </Grid>

                        <Grid size={12}>
                            <Collapse in={addParticipantRewards} timeout="auto" unmountOnExit>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl>
                                            <FormLabel id="demo-radio-buttons-group-label">Criteria Function*</FormLabel>
                                            <RadioGroup
                                                row
                                                aria-labelledby="demo-radio-buttons-group-label"
                                                name="radio-buttons-group"
                                                value={criteriaFunctionForRewards}
                                                onChange={(event) => setCriteriaFunctionForRewards(event.target.value)}
                                            >
                                                <FormControlLabel disabled value="max" control={<Radio />} label="Max" />
                                                <FormControlLabel value="count" control={<Radio />} label="Count" />
                                            </RadioGroup>
                                        </FormControl>

                                        {criteriaFunctionForRewards === 'count' && (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Count Limit"
                                                value={noOfParticipants}
                                                onChange={(event) => setNoOfParticipants(event.target.value)}
                                                placeholder="e.g., 5"
                                                type="number"
                                                sx={{ mt: 2 }}
                                                required
                                                helperText="Enter value (minimum 1)"
                                            />
                                        )}


                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl>
                                            <FormLabel id="demo-radio-buttons-group-label">Tracking Window*</FormLabel>
                                            <RadioGroup
                                                row
                                                aria-labelledby="demo-radio-buttons-group-label"
                                                name="radio-buttons-group"
                                                value={trackingWindowForRewards}
                                                onChange={(event) => setTrackingWindowForRewards(event.target.value)}
                                            >
                                                <FormControlLabel value="Forever" control={<Radio />} label="Forever" />
                                                <FormControlLabel value="Competition" control={<Radio />} label="Competition" />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </Grid>

                        <Grid size={12} sx={{ textAlign: 'right' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                disableElevation
                                sx={{ mt: 2, textTransform: 'none' }}
                                onClick={handleCreateCompetition}
                            >
                                Create Competition
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>

            {status && (
                <CustomSnackbar
                    open={!!status}
                    message={status}
                    severity={status.includes('success') ? 'success' : 'error'}
                    onClose={() => setStatus('')}
                />
            )}
        </React.Fragment>
    );
};

export default CompetitionCreateForm;